import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Calendar, User, Building2, FileText, ArrowLeft, Save, Eye, Edit } from "lucide-react";
import { toast } from "sonner";
import { salesOrderApiService, SalesOrder, SalesOrderData } from "../services/salesOrderApi";

type RouteMode = 'create' | 'edit' | 'view';

interface LocationState {
  mode: RouteMode;
  salesOrder?: SalesOrder;
}

// Form data interface that includes customerId and branchId as separate fields
interface SalesOrderFormData {
  orderNumber: string;
  orderDate: string;
  status: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  notes: string;
  refNo: string;
  refDate: string;
  totalAmount: number;
  taxAmount: number;
  grandTotal: number;
  isActive: boolean;
  customerId: number;
  branchId: number;
}

export function SalesOrderInformationForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, salesOrder } = (location.state as LocationState) || { mode: 'create' as RouteMode };
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<SalesOrderFormData>({
    orderNumber: '', // New field
    orderDate: new Date().toISOString(),
    status: 'DRAFT',
    effectiveStartDate: new Date().toISOString(),
    effectiveEndDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
    notes: '',
    refNo: '',
    refDate: new Date().toISOString(),
    totalAmount: 0,
    taxAmount: 0,
    grandTotal: 0,
    isActive: true,
    customerId: 1, // Default to first customer
    branchId: 1    // Default to first branch
  });

  // Mock data for customers and branches (in real app, these would come from APIs)
  const [customers] = useState([
    { id: 1, customerName: 'Bharti', customerCode: 'C001', firmName: 'IN-ZCX' },
    { id: 2, customerName: 'Customer 2', customerCode: 'C002', firmName: 'Firm 2' },
  ]);

  const [branches] = useState([
    { id: 1, branchName: 'InBoo', branchCode: 'B1' },
    { id: 2, branchName: 'Branch 2', branchCode: 'B2' },
  ]);

  // Populate form data when editing or viewing
  useEffect(() => {
    if (salesOrder && (mode === 'edit' || mode === 'view')) {
      setFormData({
        orderNumber: salesOrder.soNumber || '', // Populate new field
        orderDate: salesOrder.orderDate || new Date().toISOString(),
        status: salesOrder.status || 'Draft',
        effectiveStartDate: salesOrder.effectiveStartDate || new Date().toISOString(),
        effectiveEndDate: salesOrder.effectiveEndDate || new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
        notes: salesOrder.notes || '',
        refNo: salesOrder.refNo || '',
        refDate: salesOrder.refDate || new Date().toISOString(),
        totalAmount: salesOrder.totalAmount || 0,
        taxAmount: salesOrder.taxAmount || 0,
        grandTotal: salesOrder.grandTotal || 0,
        isActive: salesOrder.isActive ?? true,
        customerId: salesOrder.customerId || 1,
        branchId: salesOrder.branchId || 1
      });
    }
  }, [salesOrder, mode]);

  const handleInputChange = (field: keyof SalesOrderFormData, value: string | boolean | number | "indeterminate") => {
    // Handle checkbox indeterminate state by converting to false
    const finalValue = value === "indeterminate" ? false : value;
    setFormData(prev => ({
      ...prev,
      [field]: finalValue
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if (mode === 'create') {
        // Extract customerId and branchId from formData and pass them separately
        const { customerId, branchId, ...orderData } = formData;
        await salesOrderApiService.createSalesOrder(orderData, customerId, branchId);
        toast.success('Sales order created successfully!');
      } else if (mode === 'edit' && salesOrder) {
        // Extract customerId and branchId from formData and pass them separately
        const { customerId, branchId, ...orderData } = formData;
        await salesOrderApiService.updateSalesOrder(salesOrder.id.toString(), orderData, customerId, branchId, salesOrder);
        toast.success('Sales order updated successfully!');
      }
      
      navigate('/sales-orders/listing');
    } catch (error) {
      // console.error('Error saving sales order:', error);
      toast.error(mode === 'create' ? 'Failed to create sales order' : 'Failed to update sales order');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const getPageTitle = () => {
    switch (mode) {
      case 'create': return 'Create New Sales Order';
      case 'edit': return 'Edit Sales Order';
      case 'view': return 'View Sales Order';
      default: return 'Sales Order';
    }
  };

  const getPageIcon = () => {
    switch (mode) {
      case 'create': return <FileText className="w-6 h-6" />;
      case 'edit': return <Edit className="w-6 h-6" />;
      case 'view': return <Eye className="w-6 h-6" />;
      default: return <FileText className="w-6 h-6" />;
    }
  };

  const isViewMode = mode === 'view';

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
              {getPageIcon()}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{getPageTitle()}</h1>
              <p className="text-gray-600 mt-1">
                {mode === 'create' && 'Create a new sales order with customer and product details'}
                {mode === 'edit' && 'Update sales order information'}
                {mode === 'view' && 'View sales order details'}
              </p>
            </div>
          </div>
        </div>
        
        {!isViewMode && (
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
            >
              Draft
            </Button>
            <Button 
              onClick={handleSave}
              disabled={loading}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : mode === 'create' ? 'Create Order' : 'Update Order'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-600" />
                Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="orderNumber" className="text-gray-700"># Order Number</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="orderNumber"
                      value={formData.orderNumber}
                      onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                      placeholder="Enter order number"
                      className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                      disabled={isViewMode}
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        const newOrderNumber = `SO-${Date.now()}`;
                        handleInputChange('orderNumber', newOrderNumber);
                      }}
                      disabled={isViewMode || loading}
                      className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                    >
                      Generate
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="orderDate" className="text-gray-700">Order Date *</Label>
                  <Input
                    id="orderDate"
                    type="date"
                    value={formData.orderDate ? new Date(formData.orderDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleInputChange('orderDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    disabled={isViewMode}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="effectiveStartDate" className="text-gray-700">Effective Start Date</Label>
                  <Input
                    id="effectiveStartDate"
                    type="date"
                    value={formData.effectiveStartDate ? new Date(formData.effectiveStartDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleInputChange('effectiveStartDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
                    placeholder="dd/mm/yyyy"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    disabled={isViewMode}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status" className="text-gray-700">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleInputChange('status', value)}
                    disabled={isViewMode}
                  >
                    <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      <SelectItem value="SHIPPED">Shipped</SelectItem>
                      <SelectItem value="DELIVERED">Delivered</SelectItem>
                      <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="effectiveEndDate" className="text-gray-700">Effective End Date</Label>
                  <Input
                    id="effectiveEndDate"
                    type="date"
                    value={formData.effectiveEndDate ? new Date(formData.effectiveEndDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleInputChange('effectiveEndDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
                    placeholder="dd/mm/yyyy"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    disabled={isViewMode}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="totalAmount" className="text-gray-700">Total Amount</Label>
                  <Input
                    id="totalAmount"
                    value={formData.totalAmount}
                    onChange={(e) => handleInputChange('totalAmount', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    disabled={isViewMode}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="refNo" className="text-gray-700">Reference Number</Label>
                  <Input
                    id="refNo"
                    value={formData.refNo}
                    onChange={(e) => handleInputChange('refNo', e.target.value)}
                    placeholder="Enter reference number"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    disabled={isViewMode}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="refDate" className="text-gray-700">Reference Date</Label>
                  <Input
                    id="refDate"
                    type="date"
                    value={formData.refDate ? new Date(formData.refDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleInputChange('refDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
                    placeholder="dd/mm/yyyy"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    disabled={isViewMode}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes" className="text-gray-700">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Enter any additional notes or comments"
                  rows={3}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={isViewMode}
                />
              </div>

              {/* Financial Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="taxAmount" className="text-gray-700">Tax Amount</Label>
                  <Input
                    id="taxAmount"
                    value={formData.taxAmount}
                    onChange={(e) => handleInputChange('taxAmount', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    disabled={isViewMode}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="grandTotal" className="text-gray-700">Grand Total</Label>
                  <Input
                    id="grandTotal"
                    value={formData.grandTotal}
                    onChange={(e) => handleInputChange('grandTotal', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    disabled={isViewMode}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer & Branch Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary-600" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customerId" className="text-gray-700">Customer Name *</Label>
                  <Select
                    value={formData.customerId ? formData.customerId.toString() : ""}
                    onValueChange={(value) => handleInputChange('customerId', parseInt(value) || 0)}
                    disabled={isViewMode}
                  >
                    <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id.toString()}>
                          {customer.customerName} ({customer.customerCode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="branchId" className="text-gray-700">Branch *</Label>
                  <Select
                    value={formData.branchId ? formData.branchId.toString() : ""}
                    onValueChange={(value) => handleInputChange('branchId', parseInt(value) || 0)}
                    disabled={isViewMode}
                  >
                    <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id.toString()}>
                          {branch.branchName} ({branch.branchCode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="customerAddress" className="text-gray-700">Customer Address</Label>
                <Input
                  id="customerAddress"
                  value="Customer address will be auto-filled when customer is selected"
                  className="border-primary-200 bg-gray-50 text-gray-500"
                  disabled={true}
                  readOnly
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status & Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Status & Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) => handleInputChange('isActive', checked)}
                    disabled={isViewMode}
                  />
                  <Label htmlFor="isActive" className="text-gray-700">Active Order</Label>
                </div>
                <p className="text-sm text-gray-500">
                  Active orders are visible in the system and can be processed
                </p>
              </div>

              {salesOrder && (
                <div className="space-y-2">
                  <div className="text-sm">
                    <p className="text-gray-500">Created</p>
                    <p className="font-medium">{new Date(salesOrder.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="text-sm">
                    <p className="text-gray-500">Last Updated</p>
                    <p className="font-medium">{new Date(salesOrder.updatedAt).toLocaleDateString()}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          {!isViewMode && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : mode === 'create' ? 'Create Order' : 'Update Order'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
